import React, { useState, useEffect } from 'react';
import {
    Box, Container, Typography, Grid, Card, Button,
    alpha, Stack, Chip, Avatar, Divider, Paper, Fade, Grow,
} from '@mui/material';
import {
    School as SchoolIcon,
    LocationOn as LocationIcon,
    CheckCircle as CheckIcon,
    MenuBook as BookIcon,
    EmojiEvents as TrophyIcon,
    Groups as GroupsIcon,
    LaptopMac as LmsIcon,
    CloudUpload as UploadIcon,
    Assignment as CourseIcon,
    Person as PersonIcon,
    Security as SecurityIcon,
    School as CertIcon,
    Dashboard as DashboardIcon,
    ArrowBack as ArrowBackIcon,
    Phone as PhoneIcon,
    Email as EmailIcon,
    Engineering as EngineeringIcon,
    Build as BuildIcon,
    ElectricBolt as ElectricIcon,
    Architecture as ArchIcon,
    Apartment as FacilityIcon,
    BusinessCenter as BusinessIcon,
    AdminPanelSettings as AdminIcon,
    Hotel as HotelIcon,
    LocalHospital as HealthIcon,
    FavoriteBorder as CareIcon,
    LocalShipping as LogisticsIcon,
    ViewModule as ModuleIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Navbar from '../Navbar';
import HomeFooter from '../HomeFooter';

const BRAND_GRADIENT = 'linear-gradient(135deg, #1d748b 0%, #52d370 100%)';
const NEWTON_COLOR = '#6f42c1';

// ==================== NEWTON DATA ====================
const newtonData = {
    name: 'Newton International College',
    shortName: 'NIC',
    tagline: 'Innovation Through Education',
    color: NEWTON_COLOR,
    established: '2005',
    location: 'Singapore',
    type: 'Higher Education & Digital Learning Platform',
    about: 'Newton International College is a Singapore-based educational institution offering professional Diploma, Higher Diploma and Advanced Diploma programmes across Engineering, Management, Hospitality, Health Science, Caregiving and Logistics.',
    lmsIntro: 'We developed and implemented a customized Learning Management System (LMS) for Newton International College to support its digital learning environment and provide students with centralized access to academic resources and learning activities.',

    // LMS Solutions
    lmsSolutions: [
        {
            title: 'Learning Management',
            desc: 'Centralized platform for digital learning and academic resources.',
            icon: <LmsIcon />,
        },
        {
            title: 'Course Management',
            desc: 'Organizes courses, modules and programme-related content.',
            icon: <CourseIcon />,
        },
        {
            title: 'Student Portal',
            desc: 'Provides students with a centralized learning environment.',
            icon: <PersonIcon />,
        },
        {
            title: 'Digital Learning',
            desc: 'Supports online access to course materials and learning resources.',
            icon: <BookIcon />,
        },
        {
            title: 'Academic Management',
            desc: 'Helps manage academic content and student learning activities.',
            icon: <DashboardIcon />,
        },
        {
            title: 'Assessment Management',
            desc: 'Supports examinations, coursework and academic assessments.',
            icon: <UploadIcon />,
        },
        {
            title: 'Module Management',
            desc: 'Organizes individual modules and their associated learning content.',
            icon: <ModuleIcon />,
        },
        {
            title: 'Student Learning Resources',
            desc: 'Provides structured access to educational materials.',
            icon: <GroupsIcon />,
        },
        {
            title: 'E-Learning Platform',
            desc: 'Extends traditional classroom education with digital learning capabilities.',
            icon: <CertIcon />,
        },
    ],

    // Key Academic Areas with icons
    academicAreas: [
        { name: 'Engineering', icon: <EngineeringIcon /> },
        { name: 'Civil Engineering', icon: <ArchIcon /> },
        { name: 'Electrical Engineering', icon: <ElectricIcon /> },
        { name: 'Mechanical Engineering', icon: <BuildIcon /> },
        { name: 'Facilities Management', icon: <FacilityIcon /> },
        { name: 'Business Management', icon: <BusinessIcon /> },
        { name: 'Management', icon: <AdminIcon /> },
        { name: 'Hospitality & Tourism', icon: <HotelIcon /> },
        { name: 'Health Care Science', icon: <HealthIcon /> },
        { name: 'Caregiving', icon: <CareIcon /> },
        { name: 'Logistics & Supply Chain Management', icon: <LogisticsIcon /> },
    ],

    // Quick info
    quickInfo: [
        { label: 'Client', value: 'Newton International College', icon: <SchoolIcon /> },
        // { label: 'Location', value: 'Singapore', icon: <LocationIcon /> },
        { label: 'Solution', value: 'Customized Learning Management System (LMS)', icon: <LmsIcon /> },
        { label: 'Platform', value: 'Newton International College LMS', icon: <DashboardIcon /> },
    ],

    // Technologies
    technologies: [
        'Learning Management System (LMS)',
        'Student Portal',
        'Course Management',
        'Module Management',
        'Digital Education',
        'Academic Management',
        'Assessment Management',
        'E-Learning Platform',
    ],
};

const NewtonCollegePage = () => {
    const [mounted, setMounted] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        setMounted(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    if (!mounted) return null;

    return (
        <Box sx={{ width: '100%', minHeight: '100vh', bgcolor: '#f8f9fa' }}>
            <Navbar />

            {/* ==================== HERO ==================== */}
            <Box
                sx={{
                    background: BRAND_GRADIENT,
                    pt: { xs: 8, md: 10 },
                    pb: { xs: 12, md: 16 },
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                <Box sx={{ position: 'absolute', top: '10%', right: '5%', width: 350, height: 350, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.25) 0%, transparent 70%)' }} />
                <Box sx={{ position: 'absolute', bottom: '5%', left: '5%', width: 250, height: 250, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 70%)' }} />

                <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
                    {/* Back Button */}
                    {/* <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate('/colleges')}
                        sx={{
                            mb: 3,
                            color: 'white',
                            textTransform: 'none',
                            fontWeight: 600,
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                        }}
                    >
                        Back to Colleges
                    </Button> */}

                    <Fade in timeout={800}>
                        <Box>
                            {/* <Chip
                                label="CLIENT PROJECT"
                                sx={{
                                    mb: 3,
                                    bgcolor: 'rgba(255,255,255,0.2)',
                                    color: 'white',
                                    fontWeight: 700,
                                    backdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(255,255,255,0.3)',
                                }}
                            /> */}

                            <Typography
                                variant="h1"
                                sx={{
                                    fontSize: { xs: '2.2rem', md: '3.5rem' },
                                    fontWeight: 800,
                                    mb: 2,
                                    color: 'white',
                                    lineHeight: 1.2,
                                }}
                            >
                                {newtonData.name}
                            </Typography>

                            <Typography
                                variant="h6"
                                sx={{
                                    color: 'rgba(255,255,255,0.95)',
                                    maxWidth: 800,
                                    fontWeight: 400,
                                    mb: 3,
                                }}
                            >
                                {newtonData.type}
                            </Typography>

                            <Stack direction="row" spacing={2} flexWrap="wrap">
                                {/* <Chip
                                    icon={<LocationIcon sx={{ color: 'white !important' }} />}
                                    label="Singapore"
                                    sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600 }}
                                /> */}
                                <Chip
                                    icon={<LmsIcon sx={{ color: 'white !important' }} />}
                                    label="Customized LMS"
                                    sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600 }}
                                />
                                <Chip
                                    icon={<SchoolIcon sx={{ color: 'white !important' }} />}
                                    label="Diploma & Higher Diploma"
                                    sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600 }}
                                />
                            </Stack>
                        </Box>
                    </Fade>
                </Container>
            </Box>

            {/* ==================== OVERVIEW CARD ==================== */}
            <Container maxWidth="xl" sx={{ mt: -10, mb: 5, position: 'relative', zIndex: 2 }}>
                <Grow in timeout={1000}>
                    <Paper elevation={24} sx={{ p: { xs: 3, md: 5 }, borderRadius: 4, bgcolor: 'white' }}>
                        <Grid container spacing={4}>
                            <Grid item xs={12} md={8}>
                                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                                    <Avatar
                                        sx={{
                                            width: 70,
                                            height: 70,
                                            background: NEWTON_COLOR,
                                            fontWeight: 800,
                                            fontSize: '1.5rem',
                                        }}
                                    >
                                        {newtonData.shortName}
                                    </Avatar>
                                    <Box>
                                        <Typography variant="h4" sx={{ fontWeight: 800 }}>
                                            {newtonData.name}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {newtonData.type}
                                        </Typography>
                                    </Box>
                                </Stack>

                                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                                    {newtonData.about}
                                </Typography>

                                <Typography variant="body1" color="text.secondary">
                                    {newtonData.lmsIntro}
                                </Typography>
                            </Grid>

                            {/* Quick Info */}
                            <Grid item xs={12} md={4}>
                                <Stack spacing={2}>
                                    {newtonData.quickInfo.map((info) => (
                                        <Paper
                                            key={info.label}
                                            sx={{ p: 2, bgcolor: alpha(NEWTON_COLOR, 0.08), borderRadius: 2 }}
                                        >
                                            <Stack direction="row" spacing={2} alignItems="center">
                                                <Box sx={{ color: NEWTON_COLOR }}>{info.icon}</Box>
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {info.label}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                        {info.value}
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                        </Paper>
                                    ))}
                                </Stack>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grow>
            </Container>

            {/* ==================== LMS SOLUTIONS ==================== */}
            <Container maxWidth="xl" sx={{ py: 4 }}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
                    <LmsIcon sx={{ color: NEWTON_COLOR, fontSize: 36 }} />
                    <Typography variant="h4" sx={{ fontWeight: 800 }}>
                        LMS Solutions
                    </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                    Key modules we developed for the Newton International College learning platform
                </Typography>

                <Grid container spacing={3}>
                    {newtonData.lmsSolutions.map((solution, i) => (
                        <Grid item xs={12} sm={6} md={4} key={solution.title}>
                            <Grow in timeout={(i + 1) * 80}>
                                <Card
                                    sx={{
                                        p: 3,
                                        borderRadius: 3,
                                        height: '100%',
                                        boxShadow: '0 8px 30px rgba(0,0,0,0.06)',
                                        transition: 'all 0.25s',
                                        '&:hover': {
                                            transform: 'translateY(-6px)',
                                            boxShadow: `0 20px 40px ${alpha(NEWTON_COLOR, 0.2)}`,
                                        },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 52,
                                            height: 52,
                                            borderRadius: 2,
                                            bgcolor: alpha(NEWTON_COLOR, 0.12),
                                            color: NEWTON_COLOR,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mb: 2,
                                        }}
                                    >
                                        {solution.icon}
                                    </Box>
                                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                                        {solution.title}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {solution.desc}
                                    </Typography>
                                </Card>
                            </Grow>
                        </Grid>
                    ))}
                </Grid>
            </Container>

            {/* ==================== KEY ACADEMIC AREAS ==================== */}
            <Box sx={{ bgcolor: '#f8f9fa', py: 6 }}>
                <Container maxWidth="xl">
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
                        <SchoolIcon sx={{ color: NEWTON_COLOR, fontSize: 36 }} />
                        <Typography variant="h4" sx={{ fontWeight: 800 }}>
                            Key Academic Areas
                        </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                        Diploma, Higher Diploma and Advanced Diploma disciplines offered at Newton International College
                    </Typography>

                    <Grid container spacing={3}>
                        {newtonData.academicAreas.map((area, i) => (
                            <Grid item xs={12} sm={6} md={4} lg={3} key={area.name}>
                                <Grow in timeout={(i + 1) * 80}>
                                    <Card
                                        sx={{
                                            p: 3,
                                            borderRadius: 3,
                                            height: '100%',
                                            boxShadow: '0 8px 30px rgba(0,0,0,0.06)',
                                            borderLeft: `4px solid ${NEWTON_COLOR}`,
                                            transition: 'all 0.25s',
                                            '&:hover': {
                                                transform: 'translateY(-6px)',
                                                boxShadow: `0 20px 40px ${alpha(NEWTON_COLOR, 0.2)}`,
                                            },
                                        }}
                                    >
                                        <Stack direction="row" spacing={2} alignItems="center">
                                            <Avatar
                                                sx={{
                                                    bgcolor: alpha(NEWTON_COLOR, 0.12),
                                                    color: NEWTON_COLOR,
                                                    width: 44,
                                                    height: 44,
                                                }}
                                            >
                                                {area.icon}
                                            </Avatar>
                                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                                {area.name}
                                            </Typography>
                                        </Stack>
                                    </Card>
                                </Grow>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* ==================== TECHNOLOGY & SOLUTION ==================== */}
            <Container maxWidth="xl" sx={{ py: 6 }}>
                <Paper sx={{ p: 4, borderRadius: 3 }}>
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                        <SecurityIcon sx={{ color: NEWTON_COLOR, fontSize: 32 }} />
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>
                            Technology & Solution
                        </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Technologies and solutions used to build the Newton LMS platform
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {newtonData.technologies.map((tech) => (
                            <Chip
                                key={tech}
                                label={tech}
                                sx={{
                                    bgcolor: alpha(NEWTON_COLOR, 0.12),
                                    color: NEWTON_COLOR,
                                    fontWeight: 600,
                                    mb: 1,
                                }}
                            />
                        ))}
                    </Stack>
                </Paper>
            </Container>


        </Box>
    );
};

export default NewtonCollegePage;